import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { track } from "../lib/analytics";

/**
 * Reinforcement modal — "Why your employees will love this."
 *
 * Opens from the `/employers` result screen on click of the secondary
 * CTA. Five-section pitch:
 *   01. Opening beat  (what employees will see)
 *   02. The number    (a concrete employee-side premium)
 *   03. Three things  (rate-locked, portable, cash value)
 *   04. Chronic care  (reveal, coral-soft background)
 *   05. You too       (closes by reframing the employer)
 *
 * Behavior:
 *   - backdrop click / Esc / close-button all dismiss
 *   - body scroll locked while open
 *   - close button receives focus on open for keyboard users
 *   - primary CTA routes forward to /portal/proposal/{id}
 */
export default function ReinforcementModal({
    open,
    onClose,
    onPrimaryCta,
    sampleBiweekly,
    coverageAmount = 50000,
}) {
    const { t } = useTranslation();
    const closeBtnRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        track("reinforcement_modal_view", {});
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        // focus the close button for keyboard users
        setTimeout(() => closeBtnRef.current?.focus(), 20);
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        document.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prevOverflow;
            document.removeEventListener("keydown", onKey);
        };
    }, [open, onClose]);

    if (!open) return null;

    const number = typeof sampleBiweekly === "number"
        ? `$${sampleBiweekly.toFixed(2)}`
        : "$27.26";
    const coverageLabel = `$${coverageAmount.toLocaleString()}`;

    const primary = () => {
        track("reinforcement_modal_primary_cta", {});
        onPrimaryCta?.();
    };

    const dismiss = () => {
        track("reinforcement_modal_dismiss", {});
        onClose?.();
    };

    return (
        <div
            className="reveal-overlay active"
            data-testid="reinforcement-modal-overlay"
            onClick={(e) => {
                // backdrop click dismisses when outside the modal card
                if (e.target === e.currentTarget) dismiss();
            }}
        >
            <div
                className="reveal-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="reveal-title"
                data-testid="reinforcement-modal"
            >
                <button
                    ref={closeBtnRef}
                    className="reveal-close"
                    aria-label={t("reinforcement.closeAria")}
                    onClick={dismiss}
                    data-testid="reinforcement-modal-close"
                />

                {/* Opening beat */}
                <div className="reveal-open reveal-section">
                    <div className="reveal-section-label">
                        {t("reinforcement.s1.label")}
                    </div>
                    <h2 id="reveal-title">
                        {t("reinforcement.s1.h2a")}{" "}
                        <em>{t("reinforcement.s1.h2b")}</em>
                    </h2>
                    <p>{t("reinforcement.s1.body")}</p>
                </div>

                {/* The number */}
                <div className="reveal-number">
                    <div className="sample-ctx">
                        {t("reinforcement.s2.ctx")}
                    </div>
                    <div className="big-number" data-testid="reinforcement-big-number">
                        {number}
                    </div>
                    <div className="freq">
                        {t("reinforcement.s2.freq", { coverage: coverageLabel })}
                    </div>
                    <p className="caption">{t("reinforcement.s2.caption")}</p>
                </div>

                {/* Three things employees own */}
                <div className="reveal-section reveal-benefits">
                    <div className="reveal-section-label">
                        {t("reinforcement.s3.label")}
                    </div>
                    <h3>
                        {t("reinforcement.s3.h3a")}{" "}
                        <em>{t("reinforcement.s3.h3b")}</em>
                    </h3>
                    <ul className="benefit-list">
                        <li>
                            <div className="benefit-num">01</div>
                            <div>
                                <div className="benefit-title">
                                    {t("reinforcement.s3.item1.title")}
                                </div>
                                <div
                                    className="benefit-body"
                                    dangerouslySetInnerHTML={{
                                        __html: t("reinforcement.s3.item1.body", {
                                            number,
                                        }),
                                    }}
                                />
                            </div>
                        </li>
                        <li>
                            <div className="benefit-num">02</div>
                            <div>
                                <div className="benefit-title">
                                    {t("reinforcement.s3.item2.title")}
                                </div>
                                <div
                                    className="benefit-body"
                                    dangerouslySetInnerHTML={{
                                        __html: t("reinforcement.s3.item2.body"),
                                    }}
                                />
                            </div>
                        </li>
                        <li>
                            <div className="benefit-num">03</div>
                            <div>
                                <div className="benefit-title">
                                    {t("reinforcement.s3.item3.title")}
                                </div>
                                <div
                                    className="benefit-body"
                                    dangerouslySetInnerHTML={{
                                        __html: t("reinforcement.s3.item3.body"),
                                    }}
                                />
                            </div>
                        </li>
                    </ul>
                </div>

                {/* Chronic care reveal */}
                <div className="reveal-chronic reveal-section">
                    <div className="reveal-section-label">
                        {t("reinforcement.s4.label")}
                    </div>
                    <h3>
                        {t("reinforcement.s4.h3a")}{" "}
                        <em>{t("reinforcement.s4.h3b")}</em>
                    </h3>
                    <p
                        dangerouslySetInnerHTML={{
                            __html: t("reinforcement.s4.p1"),
                        }}
                    />
                    <p>{t("reinforcement.s4.p2")}</p>
                    <p>{t("reinforcement.s4.p3")}</p>
                </div>

                {/* You too */}
                <div className="reveal-close-section reveal-section">
                    <div className="reveal-section-label">
                        {t("reinforcement.s5.label")}
                    </div>
                    <h3>
                        {t("reinforcement.s5.h3a")}{" "}
                        <em>{t("reinforcement.s5.h3b")}</em>
                    </h3>
                    <p
                        dangerouslySetInnerHTML={{
                            __html: t("reinforcement.s5.p1"),
                        }}
                    />
                    <p>{t("reinforcement.s5.p2")}</p>
                    <div className="you-too">{t("reinforcement.s5.youToo")}</div>
                </div>

                {/* CTA row */}
                <div className="reveal-cta-row">
                    <button
                        type="button"
                        className="reveal-primary"
                        onClick={primary}
                        data-testid="reinforcement-modal-primary"
                    >
                        {t("reinforcement.ctaPrimary")}
                    </button>
                    <button
                        type="button"
                        className="reveal-dismiss"
                        onClick={dismiss}
                        data-testid="reinforcement-modal-dismiss"
                    >
                        {t("reinforcement.ctaDismiss")}
                    </button>
                </div>
            </div>
        </div>
    );
}
